import {getAuthConfig} from './authConfig'
import {createPkcePair, PKCE_CODE_VERIFIER_STORAGE_KEY} from './pkce'
import {safeInternalPath} from './safeRedirect'

/**
 * One-shot sessionStorage flag: the next authorize redirect must show the
 * login form (`prompt=login`) even if the auth site still has a session. Set
 * by an explicit logout and by the fallback logout in apis/helpers.ts, which
 * revoke the auth site's sessions but only best effort (bounded by a timeout).
 * AuthRedirect consumes it (AB#6617).
 */
export const FORCE_LOGIN_STORAGE_KEY = 'mbai-force-login'

export const requestForceLogin = (): void => {
  sessionStorage.setItem(FORCE_LOGIN_STORAGE_KEY, '1')
}

export const isForceLoginRequested = (): boolean =>
  sessionStorage.getItem(FORCE_LOGIN_STORAGE_KEY) === '1'

export const clearForceLogin = (): void => {
  sessionStorage.removeItem(FORCE_LOGIN_STORAGE_KEY)
}

export interface BuildAuthorizeUrlOptions {
  /** Adds `prompt=login`: the auth site ends its session and shows the login form. */
  forceLogin?: boolean
}

/**
 * Build the PKCE authorize URL for the existing OAuth login (auth.montblanc.ai),
 * and stash the code verifier for the callback to consume.
 *
 * Returns `/auth/api/authorize/` itself, no longer wrapped in
 * `/login?next=<authorize>` (AB#6617): the auth site's login page always
 * renders the form, even with a valid session, so the wrapper made users type
 * their password on every redirect. The authorize endpoint answers with a code
 * straight away while the auth site's session is valid, and sends the browser
 * to `/login?next=<authorize>` itself when there is none. That is what makes
 * the silent re-authorize after an expired token (apis/helpers.ts) silent.
 *
 * Extracted out of AuthRedirect (the top-level-redirect login flow) so the
 * Teams tab's popup sign-in (AB#6096, `microsoftTeams.authentication.authenticate()`)
 * can point at the exact same login/callback machinery — same redirect_uri, same
 * `/authentication/code/callback/` route, no new Entra/auth-provider allowlist
 * entry needed for the popup to work. Without a session the popup still lands
 * on the login form, via the authorize endpoint's own redirect.
 */
export const buildAuthorizeUrl = async (
  originalUrl: string,
  {forceLogin = false}: BuildAuthorizeUrlOptions = {},
): Promise<string> => {
  const safeOriginalUrl = safeInternalPath(originalUrl)
  const {authProviderBaseUrl, clientId, redirectUri, scope} = getAuthConfig()
  const {codeChallenge, codeVerifier} = await createPkcePair()
  const authorizeUrl = new URL(`${authProviderBaseUrl}/auth/api/authorize/`)

  sessionStorage.setItem(PKCE_CODE_VERIFIER_STORAGE_KEY, codeVerifier)

  authorizeUrl.searchParams.set('client_id', clientId)
  authorizeUrl.searchParams.set('response_type', 'code')
  authorizeUrl.searchParams.set('redirect_uri', redirectUri)
  authorizeUrl.searchParams.set('scope', scope)
  authorizeUrl.searchParams.set('state', safeOriginalUrl)
  authorizeUrl.searchParams.set('code_challenge', codeChallenge)
  authorizeUrl.searchParams.set('code_challenge_method', 'S256')
  if (forceLogin) {
    authorizeUrl.searchParams.set('prompt', 'login')
  }

  return authorizeUrl.toString()
}
